import { config } from "@fidget/common/config/config";
import { staticConfig } from "@fidget/common/config/static-config";
import { observable } from "mobx";
import { UrlTagsProactiveMessages } from "@fidget/common/models/url-tags-proactive-messages";
import {
  Call,
  CallsApi,
  Configuration,
  FilesApi,
  GuestAssignee,
  GuestProfileFormPatch,
  InputValue,
  LeadCtx,
  LeadProfile,
  LimitedAgentProfile,
  Meeting,
  MeetingsApi,
  MeetingSlot,
  OplAddressCity,
  OplAddressCityQuery,
  OplAddressApi,
  OplAddressStreet,
  OplAddressStreetQuery,
  OplAddressZipCode,
  OplAddressZipCodeQuery,
  ProactiveMsgApi,
  ProfanitiesApi,
  UrlTagMappingsApi,
  UsersApi,
  WidgetFormSubmission,
  WidgetFormSubmissionWithWidget,
  WidgetsFormsSubmissionsApi,
  MainNotificationDto,
  MainNotificationsApi,
  TagGroupsApi,
  TagGroupDto
} from "@closerplatform/spinner-openapi";
import { debounce, head } from "lodash";
import { InitialContext } from "./initialContextService";
import { IAttachment } from "@fidget/common/models/chat/chatItems";
import { noop } from "@fidget/common/utils/noop";
import { mapUploadResponse } from "@fidget/common/utils/fileUploadResponses";
import { Timed } from "@fidget/common/utils/timed";
import { IFileUploadInfo } from "@fidget/common/models/fileUpload";
import { IBrowserWindowSize } from "@fidget/common/models/browserWindowSize";
import { logger } from "@fidget/common/utils";
import { sequentialize } from "@fidget/common/utils/sequentialize";
import { SharedSpinnerService } from "@fidget/common/models/shared/spinner/sharedSpinnerService";
import { Language } from "@fidget/common/models/language";
import { getWheelhouseDownloadUrl } from "@fidget/common/utils/urlMappingHelpers";

export interface IForeSpinnerService extends SharedSpinnerService {
  setApiKey(apiKey: string | undefined): void;
  setFingerprint(fingerprint: string): void;
  setOrgId(orgId: string): void;
  createCall(xDeviceId: string, invitee: string): Promise<Call>;
  signUpGuest(signUpContext?: InitialContext): Promise<LeadCtx>;
  reinitLead(currentUserId: string, currentApiKey: string, secondaryApiKey: string): Promise<void>;
  continueConversation(token: string): Promise<LeadCtx>;
  joinMeeting(token: string): Promise<LeadCtx>;
  cancelMeeting(token: string): Promise<LeadCtx>;
  cancelMeetingScheduling(token: string): Promise<void>;
  scheduleMeeting(token: string, start: number, duration: number): Promise<Meeting>;
  getMeetingSlots(
    token: string,
    startTimestamp: number,
    endTimestamp: number
  ): Promise<Array<MeetingSlot>>;
  getGuestProfile(userId: string): Promise<LeadCtx>;
  getAgentProfile(id: string): Promise<LimitedAgentProfile>;
  identifyUser(user: GuestProfileFormPatch): Promise<LeadProfile>;
  patchBrowserSize(info: IBrowserWindowSize): void;
  getAssignee(userId: string): Promise<GuestAssignee>;
  getOwnLeadCtx(orgId: string): Promise<LeadCtx>;
  sendContinueConversationLink(contact?: string): Promise<void>;
  signInWithOAuth(loginHintToken: string): Promise<LeadCtx>;
  getWidgetForm(formId: string): Promise<WidgetFormSubmissionWithWidget>;
  submitWidgetForm(formId: string, answer: Array<InputValue>): Promise<WidgetFormSubmission>;
  getTagGroup(id: string): Promise<TagGroupDto>;

  /* OPL Address api */
  getOplAddressCities(body: OplAddressCityQuery): Promise<OplAddressCity[]>;
  getOplAddressStreets(body: OplAddressStreetQuery): Promise<OplAddressStreet[]>;
  getOplAddressZipCodes(body: OplAddressZipCodeQuery): Promise<OplAddressZipCode[]>;

  getMainNotification(locale: Language): Promise<MainNotificationDto | undefined>;
}

@Timed
export class ForeSpinnerService implements IForeSpinnerService {
  private static instance: IForeSpinnerService;
  private apiKey?: string;
  private fingerpint?: string;

  private orgId?: string;

  private configuration = new Configuration({ basePath: config.services.spinner.baseUrl });

  private usersApi = new UsersApi(this.configuration);
  private tagGroupsApi = new TagGroupsApi(this.configuration);
  private profanitiesApi = new ProfanitiesApi(this.configuration);
  private filesApi = new FilesApi(this.configuration);
  private meetingsApi = new MeetingsApi(this.configuration);
  private proactiveMsgApi = new ProactiveMsgApi(this.configuration);
  private urlTagMappingsApi = new UrlTagMappingsApi(this.configuration);
  private widgetsFormsSubmissionsApi = new WidgetsFormsSubmissionsApi(this.configuration);
  private oplAddressApi = new OplAddressApi(this.configuration);
  private callsApi = new CallsApi(this.configuration);
  private mainNotificationsApi = new MainNotificationsApi(this.configuration);

  public constructor() {
    // TODO: get rid of java style singleton implementation
  }

  static getInstance() {
    if (!ForeSpinnerService.instance) {
      ForeSpinnerService.instance = new ForeSpinnerService();
    }
    return ForeSpinnerService.instance;
  }

  setOrgId(orgId: string) {
    this.orgId = orgId;
  }

  setFingerprint(fingerprint: string): void {
    this.fingerpint = fingerprint;
    this.recreate();
  }

  setApiKey(apiKey: string) {
    this.apiKey = apiKey;
    this.recreate();
  }

  private recreate() {
    const { apiKeyHeader, fingerprintHeader } = staticConfig.auth;

    this.configuration = new Configuration({
      basePath: this.configuration.basePath,
      apiKey: (type) => {
        switch (type) {
          case apiKeyHeader:
            return this.apiKey!;
          case fingerprintHeader:
            return this.fingerpint!;
          default:
            return "";
        }
      }
    });

    this.oplAddressApi = new OplAddressApi(this.configuration);
    this.usersApi = new UsersApi(this.configuration);
    this.tagGroupsApi = new TagGroupsApi(this.configuration);
    this.profanitiesApi = new ProfanitiesApi(this.configuration);
    this.filesApi = new FilesApi(this.configuration);
    this.meetingsApi = new MeetingsApi(this.configuration);
    this.widgetsFormsSubmissionsApi = new WidgetsFormsSubmissionsApi(this.configuration);
    this.callsApi = new CallsApi(this.configuration);
    this.mainNotificationsApi = new MainNotificationsApi(this.configuration);
  }

  createCall(xDeviceId: string, invitee: string): Promise<Call> {
    return this.callsApi.createCall({ invitee }, xDeviceId);
  }

  signUpGuest(signUpContext?: InitialContext): Promise<LeadCtx> {
    return this.usersApi.signUpGuest({ orgId: this.orgId!, context: signUpContext });
  }

  getGuestProfile(userId: string) {
    return this.usersApi.getGuestProfile(this.orgId!, userId);
  }

  getAgentProfile(id: string): Promise<LimitedAgentProfile> {
    return this.usersApi.getLimitedAgentProfile(id).then((o) => observable(o));
  }

  getAssignee(userId: string): Promise<GuestAssignee> {
    return this.usersApi.getGuestAssignee(this.orgId!, userId);
  }

  identifyUser = sequentialize((user: GuestProfileFormPatch): Promise<LeadProfile> => {
    logger.info(`identifyUser(${JSON.stringify(user)})`);
    return this.usersApi.updateOwnGuestProfile(user, this.orgId!);
  });

  async reinitLead(
    currentUserId: string,
    currentApiKey: string,
    secondaryApiKey: string
  ): Promise<void> {
    await this.usersApi.reinitCustomer(currentUserId, {
      headers: { "X-Api-Key": currentApiKey, "X-Secondary-Api-Key": secondaryApiKey }
    });
  }

  patchBrowserSize = debounce((size: IBrowserWindowSize) => {
    this.identifyUser({
      backOfficeData: [{ key: "browserSize", value: `${size.width}x${size.height}` }]
    }).catch((e) => {
      logger.error({ message: "Couldn't patch browser size", error: e });
    });
  }, config.services.times.resizeBrowserDebounceMillis);

  continueConversation(token: string) {
    return this.usersApi.continueConversation({ token });
  }

  joinMeeting(token: string) {
    return this.usersApi.joinMeeting({ token });
  }

  cancelMeeting(token: string) {
    return this.usersApi.cancelMeetingToken({ token });
  }

  cancelMeetingScheduling(token: string) {
    return this.meetingsApi.customerCancel({ token }).then(noop);
  }

  getMeetingSlots(token: string, startTimestamp: number, endTimestamp: number) {
    return this.meetingsApi.getBotSlots({ token, start: startTimestamp, stop: endTimestamp });
  }

  scheduleMeeting(token: string, start: number, duration: number) {
    return this.meetingsApi.customerSchedule({ token, start, duration });
  }

  getOwnLeadCtx(orgId: string): Promise<LeadCtx> {
    return this.usersApi.getGuestProfileByApiKey(orgId);
  }

  sendContinueConversationLink(contact?: string) {
    return this.usersApi.continueConversationLink({ orgId: this.orgId || "", contact }).then(noop);
  }

  async uploadFile(file: File, roomId: string): Promise<IAttachment> {
    const handle = await this.filesApi.getFileHandleForAttachmentUpload(roomId);

    const form = new FormData();
    form.append("file", file);
    const fileUrl = getWheelhouseDownloadUrl(handle.fileToken);
    const response = await fetch(fileUrl, { method: "POST", body: form });

    return mapUploadResponse(response, file.type, handle.fileToken, handle.fileId);
  }

  async deleteFile(fileUpload: IFileUploadInfo) {
    const { apiKeyHeader, fingerprintHeader } = staticConfig.auth;
    if (fileUpload && fileUpload.attachment) {
      await fetch(getWheelhouseDownloadUrl(fileUpload.attachment.token), {
        method: "DELETE",
        headers: this.fingerpint
          ? { [apiKeyHeader]: this.apiKey!, [fingerprintHeader]: this.fingerpint }
          : { [apiKeyHeader]: this.apiKey! }
      });
    }
  }

  async getTemporaryToken(fileToken: string): Promise<string> {
    return (await this.filesApi.getTemporaryToken(fileToken)).temporaryToken;
  }

  signInWithOAuth(loginHintToken: string) {
    return this.usersApi.signInWithOAuth({ loginHintToken, orgId: this.orgId || "" });
  }

  getProfanities() {
    return this.profanitiesApi.getProfanities(this.orgId || "");
  }

  getWidgetForm(formId: string): Promise<WidgetFormSubmissionWithWidget> {
    return this.widgetsFormsSubmissionsApi.getWidgetFormByFormId(formId);
  }

  submitWidgetForm(formId: string, userInputs: Array<InputValue>): Promise<WidgetFormSubmission> {
    return this.widgetsFormsSubmissionsApi.submitWidgetForm({ userInputs }, formId);
  }

  getUrlTagsProactiveMessages(): Promise<UrlTagsProactiveMessages> {
    return Promise.all([
      this.urlTagMappingsApi.getUrlTagMappingsUnauthorized(this.orgId || ""),
      this.proactiveMsgApi.getProactiveMessagesUnauthorized(this.orgId || "")
    ]).then((res) => ({
      urlTagMappings: res[0],
      proactiveMessages: res[1]
    }));
  }

  getTagGroup(id: string) {
    return this.tagGroupsApi.getTagGroup(id);
  }

  getOplAddressCities(body: OplAddressCityQuery): Promise<OplAddressCity[]> {
    return this.oplAddressApi.getOplAddressCities(body);
  }

  getOplAddressStreets(body: OplAddressStreetQuery): Promise<OplAddressStreet[]> {
    return this.oplAddressApi.getOplAddressStreets(body);
  }

  getOplAddressZipCodes(body: OplAddressZipCodeQuery): Promise<OplAddressZipCode[]> {
    return this.oplAddressApi.getOplAddressZipCodes(body);
  }

  getMainNotification(locale: Language): Promise<MainNotificationDto | undefined> {
    return this.mainNotificationsApi.getMainNotifications(locale).then(head);
  }
}
